<template>
  <header class="topbar">
    <div class="topbar-content">
      <div class="topbar-start">
        <!-- Logo / Brand -->
        <RouterLink to="/dashboard" class="brand">
          <span class="brand-text">HudStore</span>
        </RouterLink>

        <!-- Command palette trigger -->
        <button class="command-trigger" @click="openCommandPalette">
          <i class="pi pi-search"></i>
          <span class="command-text">{{ t('commandPalette.placeholder').split('...')[0] }}</span>
          <kbd class="command-shortcut">{{ isMac ? '⌘' : 'Ctrl' }}K</kbd>
        </button>
      </div>

      <div class="topbar-end">
        <!-- Branch indicator -->
        <div v-if="authStore.currentBranch && !isMobile" class="branch-indicator">
          <i class="pi pi-building"></i>
          <span class="branch-name">{{
            authStore.currentBranch.display_name || authStore.currentBranch.name
          }}</span>
          <span v-if="authStore.currentBranch.is_headquarters" class="hq-badge">HQ</span>
        </div>

        <!-- Notifications (placeholder for future) -->
        <button
          class="icon-button notifications-btn"
          v-if="!isMobile"
          :title="t('common.notifications')"
          :aria-label="t('common.notifications')"
        >
          <i class="pi pi-bell"></i>
          <span class="notification-badge">3</span>
        </button>

        <!-- Table density toggle (affects every list view) -->
        <button
          class="icon-button density-btn"
          :class="{ active: compact }"
          v-if="!isMobile"
          :title="t('common.density')"
          :aria-label="t('common.density')"
          :aria-pressed="compact"
          @click="toggleDensity"
        >
          <i :class="compact ? 'pi pi-bars' : 'pi pi-list'"></i>
        </button>

        <!-- Language switcher -->
        <LanguageSwitcher />

        <!-- Dark mode toggle -->
        <DarkModeToggle />

        <!-- User menu -->
        <div class="user-menu-wrapper">
          <button
            class="user-profile-trigger"
            @click="toggleUserMenu"
            :aria-expanded="showUserMenu"
          >
            <div class="user-avatar">
              <span class="avatar-text">{{ userInitials }}</span>
            </div>
            <i class="pi pi-chevron-down trigger-chevron" :class="{ rotated: showUserMenu }"></i>
          </button>

          <Transition name="dropdown-fade">
            <div v-if="showUserMenu" class="user-dropdown" @click.stop>
              <!-- User Profile Section -->
              <div class="dropdown-header">
                <div class="header-avatar">
                  <span class="header-avatar-text">{{ userInitials }}</span>
                </div>
                <div class="header-content">
                  <div class="header-name">{{ userDisplayName }}</div>
                  <div class="header-meta">
                    <span class="header-username">{{ authStore.user?.username }}</span>
                    <RoleBadge
                      v-if="authStore.user?.role"
                      :role="authStore.user.role"
                      size="small"
                    />
                  </div>
                </div>
              </div>

              <!-- Menu Items -->
              <div class="dropdown-body">
                <RouterLink to="/dashboard" class="menu-link" @click="showUserMenu = false">
                  <div class="menu-link-icon">
                    <i class="pi pi-home"></i>
                  </div>
                  <span class="menu-link-text">{{ t('navigation.dashboard') }}</span>
                </RouterLink>
                <button class="menu-link" @click="goToSettings">
                  <div class="menu-link-icon">
                    <i class="pi pi-cog"></i>
                  </div>
                  <span class="menu-link-text">{{ t('navigation.settings') }}</span>
                </button>
                <button class="menu-link" @click="showHelp">
                  <div class="menu-link-icon">
                    <i class="pi pi-question-circle"></i>
                  </div>
                  <span class="menu-link-text">{{ t('common.help') }}</span>
                </button>
              </div>

              <!-- Logout -->
              <div class="dropdown-footer">
                <button class="logout-button" @click="handleLogout">
                  <div class="logout-icon">
                    <i class="pi pi-sign-out"></i>
                  </div>
                  <span class="logout-text">{{ t('auth.logout') }}</span>
                </button>
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { RouterLink, useRouter } from 'vue-router'
import DarkModeToggle from '@/components/ui/DarkModeToggle.vue'
import LanguageSwitcher from '@/components/ui/LanguageSwitcher.vue'
import RoleBadge from '@/components/common/RoleBadge.vue'
import { useAuthStore } from '@/stores/auth'
import { useI18n } from '@/composables/useI18n'
import { useCommandPalette } from '@/composables/useCommandPalette'
import { useDensity } from '@/composables/useDensity'

const authStore = useAuthStore()
const router = useRouter()
const { open: openCommandPalette } = useCommandPalette()
const { t } = useI18n()
const { compact, toggle: toggleDensity } = useDensity()

const isMac = computed(() => {
  if (typeof navigator === 'undefined') return false
  return navigator.platform.toUpperCase().indexOf('MAC') >= 0
})

// Reactive data
const isMobile = ref(false)
const showUserMenu = ref(false)

// Computed properties
const userDisplayName = computed(() => {
  if (authStore.user) {
    return authStore.user.full_name || authStore.user.username
  }
  return 'User'
})

const userInitials = computed(() => {
  if (authStore.user?.full_name) {
    const names = authStore.user.full_name.trim().split(' ')
    if (names.length >= 2) {
      return (names[0][0] + names[names.length - 1][0]).toUpperCase()
    }
    return names[0].substring(0, 2).toUpperCase()
  }
  if (authStore.user?.username) {
    return authStore.user.username.substring(0, 2).toUpperCase()
  }
  return 'U'
})

const userRole = computed(() => {
  if (authStore.user?.role) {
    return authStore.user.role.charAt(0).toUpperCase() + authStore.user.role.slice(1)
  }
  return 'User'
})

const emit = defineEmits<{
  logout: []
}>()

// Methods
const toggleUserMenu = () => {
  showUserMenu.value = !showUserMenu.value
}

const handleLogout = () => {
  showUserMenu.value = false
  emit('logout')
}

const goToSettings = () => {
  showUserMenu.value = false
  router.push('/settings')
}

const showHelp = () => {
  showUserMenu.value = false
  // Tech Debt: Help modal / documentation page not yet implemented
  // Issue: HUDAPI-XXX - Implement help modal or navigate to help page
}

const checkMobile = () => {
  isMobile.value = window.innerWidth <= 768
}

const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement
  if (!target.closest('.user-menu-wrapper')) {
    showUserMenu.value = false
  }
}

// Lifecycle
onMounted(() => {
  checkMobile()
  window.addEventListener('resize', checkMobile)
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile)
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
.topbar {
  position: sticky;
  top: 0;
  z-index: var(--app-z-topbar);
  height: 60px;

  /* Glass morphism effect */
  background: var(--glass-bg-heavy);
  backdrop-filter: blur(var(--glass-blur-lg));
  -webkit-backdrop-filter: blur(var(--glass-blur-lg));
  border-bottom: 1px solid var(--glass-border-light);
  box-shadow: var(--glass-shadow);

  transition: all var(--app-transition-base) cubic-bezier(0.4, 0, 0.2, 1);
}

.app-dark .topbar {
  background: var(--glass-bg-heavy);
  border-bottom: 1px solid var(--glass-border);
  box-shadow: var(--glass-shadow-lg);
}

.topbar-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 var(--app-spacing-xl);
  height: 100%;
  max-width: 1920px;
  margin: 0 auto;
}

/* Left section */
.topbar-start {
  display: flex;
  align-items: center;
  gap: var(--app-spacing-lg);
  min-width: 0;
}

/* Brand */
.brand {
  display: flex;
  align-items: center;
  text-decoration: none;
  transition: transform 0.2s ease;
}

.brand:hover {
  transform: scale(1.02);
}

.brand-text {
  font-size: 1.25rem;
  font-weight: 700;
  background: var(--app-gradient-text);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.02em;
}

.app-dark .brand-text {
  background: var(--app-gradient-text);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* Command palette trigger */
.command-trigger {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.875rem;
  background: var(--app-surface-50);
  border: 1px solid var(--app-border);
  border-radius: var(--app-radius-lg);
  cursor: pointer;
  transition: all 0.2s ease;
  color: var(--app-text-muted);
  font-size: var(--app-font-size-sm);
}

.command-trigger:hover {
  background: var(--app-surface-100);
  border-color: var(--app-surface-300);
  color: var(--app-text-secondary);
}

.app-dark .command-trigger {
  background: var(--app-surface-800);
  border-color: var(--app-surface-700);
}

.app-dark .command-trigger:hover {
  background: var(--app-surface-700);
  border-color: var(--app-surface-600);
}

.command-trigger i {
  font-size: 0.875rem;
}

.command-text {
  white-space: nowrap;
}

.command-shortcut {
  padding: 2px 6px;
  font-size: 0.6875rem;
  font-family: inherit;
  background: var(--app-surface-100);
  border: 1px solid var(--app-border);
  border-radius: 4px;
  color: var(--app-text-muted);
  margin-left: 0.25rem;
}

.app-dark .command-shortcut {
  background: var(--app-surface-700);
  border-color: var(--app-surface-600);
}

/* Right section */
.topbar-end {
  display: flex;
  align-items: center;
  gap: var(--app-spacing-md);
}

/* Branch Indicator */
.branch-indicator {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.875rem;
  background: var(--app-surface-50);
  border: 1px solid var(--app-border);
  border-radius: var(--app-radius-lg);
  color: var(--app-text-secondary);
  font-size: var(--app-font-size-sm);
}

.app-dark .branch-indicator {
  background: var(--app-surface-800);
  border-color: var(--app-surface-700);
}

.branch-indicator i {
  font-size: 0.875rem;
  color: var(--app-primary-text);
}

.branch-name {
  font-weight: 500;
  color: var(--app-text-primary);
  white-space: nowrap;
}

.branch-indicator .hq-badge {
  font-size: 0.625rem;
  font-weight: 600;
  padding: 0.125rem 0.375rem;
  background: var(--app-success-color);
  color: white;
  border-radius: 4px;
  text-transform: uppercase;
}

/* Icon Button (Notifications) */
.icon-button {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: var(--app-surface-50);
  border: 1px solid var(--app-border);
  border-radius: var(--app-radius-lg);
  cursor: pointer;
  transition: all 0.2s ease;
  color: var(--app-text-secondary);
}

.icon-button:hover {
  background: var(--app-surface-100);
  border-color: var(--app-primary-color);
  color: var(--app-primary-text);
  transform: translateY(-1px);
}

.density-btn.active {
  background: var(--app-primary-50);
  border-color: var(--app-primary-color);
  color: var(--app-primary-text);
}

.app-dark .icon-button {
  background: var(--app-surface-800);
  border-color: var(--app-surface-700);
}

.app-dark .icon-button:hover {
  background: var(--app-surface-700);
  border-color: var(--app-primary-color);
}

.notification-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  background: var(--app-gradient-primary);
  color: white;
  border-radius: 10px;
  font-size: 0.625rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(139, 30, 63, 0.3);
  animation: pulse-badge 2s ease-in-out infinite;
}

@keyframes pulse-badge {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

/* User Menu Trigger */
.user-menu-wrapper {
  position: relative;
}

.user-profile-trigger {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem;
  background: transparent;
  border: none;
  border-radius: var(--app-radius-lg);
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

.user-profile-trigger:hover {
  background: rgba(var(--app-primary-rgb), 0.08);
}

.user-profile-trigger:hover .user-avatar {
  transform: scale(1.05);
  box-shadow: 0 0 0 3px rgba(var(--app-primary-rgb), 0.15);
}

.app-dark .user-profile-trigger:hover {
  background: var(--app-surface-800);
}

.user-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--app-gradient-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(139, 30, 63, 0.25);
}

.avatar-text {
  color: white;
  font-size: 0.875rem;
  font-weight: 700;
  letter-spacing: -0.02em;
}

.trigger-chevron {
  color: var(--app-text-muted);
  font-size: 0.75rem;
  transition: transform 0.2s ease;
  flex-shrink: 0;
}

.trigger-chevron.rotated {
  transform: rotate(180deg);
}

/* User Dropdown */
.user-dropdown {
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  min-width: 280px;
  background: var(--app-surface-0);
  border: 1px solid var(--app-border-light);
  border-radius: 1rem;
  box-shadow:
    0 20px 40px rgba(0, 0, 0, 0.1),
    0 4px 12px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  z-index: 1100;
}

.app-dark .user-dropdown {
  background: var(--app-surface-900);
  border-color: var(--app-surface-700);
  box-shadow:
    0 20px 40px rgba(0, 0, 0, 0.5),
    0 4px 12px rgba(0, 0, 0, 0.3);
}

/* Dropdown Header */
.dropdown-header {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 1rem 1rem 0.875rem;
  border-bottom: 1px solid var(--app-border-light);
}

.app-dark .dropdown-header {
  border-bottom-color: var(--app-surface-700);
}

.header-avatar {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: var(--app-gradient-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(139, 30, 63, 0.3);
}

.header-avatar-text {
  color: white;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: -0.02em;
}

.header-content {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
  flex: 1;
}

.header-name {
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--app-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
}

.header-meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.header-username {
  font-size: 0.8125rem;
  color: var(--app-text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Dropdown Body */
.dropdown-body {
  padding: 0.5rem;
}

.menu-link {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 0.75rem;
  border-radius: 0.5rem;
  background: transparent;
  border: none;
  width: 100%;
  text-align: left;
  text-decoration: none;
  color: var(--app-text-primary);
  cursor: pointer;
  transition: all 0.15s ease;
}

.menu-link:hover {
  background: var(--app-surface-100);
}

.app-dark .menu-link:hover {
  background: var(--app-surface-800);
}

.menu-link-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem;
  background: var(--app-surface-100);
  color: var(--app-text-secondary);
  font-size: 0.9375rem;
  flex-shrink: 0;
  transition: all 0.15s ease;
}

.app-dark .menu-link-icon {
  background: var(--app-surface-800);
}

.menu-link:hover .menu-link-icon {
  background: rgba(var(--app-primary-rgb), 0.1);
  color: var(--app-primary-text);
  transform: scale(1.05);
}

.menu-link-text {
  font-size: 0.875rem;
  font-weight: 500;
  color: inherit;
}

/* Dropdown Footer */
.dropdown-footer {
  padding: 0.5rem;
  border-top: 1px solid var(--app-border-light);
}

.app-dark .dropdown-footer {
  border-top-color: var(--app-surface-700);
}

.logout-button {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 0.75rem;
  border-radius: 0.5rem;
  background: transparent;
  border: none;
  width: 100%;
  text-align: left;
  cursor: pointer;
  transition: all 0.15s ease;
}

.logout-button:hover {
  background: rgba(239, 68, 68, 0.08);
}

.logout-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem;
  background: color-mix(in srgb, var(--app-danger-color) 10%, transparent);
  color: var(--app-danger-color);
  font-size: 0.9375rem;
  flex-shrink: 0;
  transition: all 0.15s ease;
}

.logout-button:hover .logout-icon {
  background: color-mix(in srgb, var(--app-danger-color) 15%, transparent);
  transform: scale(1.05);
}

.logout-text {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--app-danger-color);
}

/* Dropdown Animation */
.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.dropdown-fade-enter-from {
  opacity: 0;
  transform: translateY(-8px) scale(0.96);
}

.dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px) scale(0.98);
}

/* Responsive design */
@media (max-width: 968px) {
  .topbar-content {
    padding: 0 var(--app-spacing-lg);
  }
}

@media (max-width: 768px) {
  .topbar {
    height: 56px;
  }

  .topbar-content {
    padding: 0 var(--app-spacing-md);
  }

  .topbar-start {
    gap: var(--app-spacing-md);
  }

  .topbar-end {
    gap: var(--app-spacing-sm);
  }

  .brand-text {
    font-size: 1.125rem;
  }

  .command-trigger .command-text,
  .command-trigger .command-shortcut {
    display: none;
  }

  .command-trigger {
    padding: 0.5rem;
  }

  .user-avatar {
    width: 32px;
    height: 32px;
  }

  .avatar-text {
    font-size: 0.8125rem;
  }

  .trigger-chevron {
    display: none;
  }

  .user-dropdown {
    right: -0.5rem;
    min-width: 260px;
  }
}

@media (max-width: 480px) {
  .topbar-content {
    padding: 0 var(--app-spacing-sm);
  }

  .brand-text {
    font-size: 1rem;
  }
}

/* Focus states for accessibility */
.command-trigger:focus,
.user-profile-trigger:focus,
.icon-button:focus {
  outline: 2px solid var(--app-primary-color);
  outline-offset: 2px;
}

/* Reduced motion */
@media (prefers-reduced-motion: reduce) {
  .topbar,
  .user-profile-trigger,
  .user-avatar,
  .trigger-chevron,
  .menu-link,
  .menu-link-icon,
  .logout-button,
  .logout-icon,
  .notification-badge {
    transition: none;
    animation: none;
    transform: none !important;
  }
}
</style>
